import { FormattedMessage, useIntl } from 'react-intl';
import {
  Col,
  Form,
  FormInstance,
  Image,
  Input,
  Modal,
  Radio,
  Row,
  Typography,
  Upload,
  UploadFile,
} from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  FilePdfOutlined,
  UploadOutlined,
} from '@ant-design/icons';

import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';

import { WorkOrderObservationVisibility } from '@/core/domain/workorder/WorkOrdersObservations/WorkOrdersObservations.types';
import { FloatingFormItem, Group } from '@/components/_common';
import ActionIcon from '@/components/_common/ActionIcon';
import isImageFileUrl from '@/utils/functions/isImageFileUrl';
import getFileNameFromUrl from '@/utils/functions/getFileNameFromUrl';
import isPdfFileUrl from '@/utils/functions/isPdfFileUrl';

const { Title } = Typography;

type UploadResponse = {
  uid: string;
  url: string;
};

export interface WorkOrderObservationData {
  observation: string;
  visibility: WorkOrderObservationVisibility;
  uploadFile?: UploadFile<UploadResponse>[];
  attachments?: string;
}

interface Props {
  form: FormInstance<WorkOrderObservationData>;
  editing: boolean;
  isWorkOrder?: boolean;
  isLoading?: boolean;
}

function WorkOrderObservationForm(props: Props) {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const attachments = Form.useWatch('attachments', props.form);

  const handleRemoveImage = async () => {
    return new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: 'Deseja realmente remover o arquivo?',
        content: getFileNameFromUrl(attachments),
        okText: 'Remover',
        okType: 'danger',
        cancelText: 'Cancelar',
        onOk: () => {
          props.form.setFieldsValue({
            uploadFile: undefined,
            attachments: undefined,
          });
          resolve(true);
        },
        onCancel: () => resolve(false),
      });
    });
  };

  return (
    <Form form={props.form} layout="vertical">
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Title level={5} style={{ marginBottom: '16px' }}>
            <FormattedMessage
              id={
                props.editing
                  ? 'workOrders.details.observations.update'
                  : 'workOrders.details.observations.create'
              }
            />
          </Title>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            noStyle
            name="observation"
            rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(1000)]}
            label={intl.formatMessage({
              id: 'workOrders.domain.observations.observation',
            })}
          >
            <Input.TextArea
              showCount
              maxLength={1000}
              autoSize={{ minRows: 4 }}
              disabled={props.isLoading}
            />
          </FloatingFormItem>
        </Col>

        {props.isWorkOrder && (
          <>
            <Form.Item name="attachments" noStyle hidden />

            {!attachments && (
              <Col span={24}>
                <Form.Item
                  name="uploadFile"
                  valuePropName="fileList"
                  getValueFromEvent={(event) => {
                    if (Array.isArray(event)) return event;

                    return event && event.fileList;
                  }}
                  rules={[
                    {
                      validator: (_, fileList) => {
                        if (!fileList || fileList.length === 0)
                          return Promise.resolve();

                        const file = fileList[0];
                        const isPdf = file.type === 'application/pdf';
                        const isTooLarge = file.size > 1024 * 1024;

                        if (isPdf && isTooLarge) {
                          return Promise.reject(
                            new Error(
                              'O arquivo PDF não pode ter mais que 1MB.',
                            ),
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Upload
                    accept="image/jpeg, image/png, application/pdf"
                    multiple={false}
                    customRequest={({ file, onError, onSuccess }) => {
                      if (typeof file !== 'string') {
                        const isPdf = file.type === 'application/pdf';
                        const isTooLarge = file.size > 1024 * 1024; // 1MB

                        if (isPdf && isTooLarge) {
                          return onError?.(
                            new Error('O arquivo PDF excede 1MB.'),
                          );
                        }
                      }

                      try {
                        !!onSuccess && onSuccess([file]);
                      } catch (error: unknown) {
                        !!onError && onError(error as Error);
                      }
                    }}
                    maxCount={1}
                    type="drag"
                    disabled={props.isLoading}
                  >
                    <Group gap={10} position="center">
                      <UploadOutlined style={{ fontSize: 16 }} />
                      Importar arquivo
                    </Group>
                  </Upload>
                </Form.Item>
              </Col>
            )}

            {!!attachments && (
              <Col span={24} style={{ margin: '12px 0' }}>
                <Upload
                  listType="picture"
                  defaultFileList={[
                    {
                      uid: attachments,
                      name: getFileNameFromUrl(attachments),
                      url: attachments,
                      thumbUrl: attachments,
                    },
                  ]}
                  onRemove={handleRemoveImage}
                  itemRender={(_, file) => (
                    <div
                      className="ant-upload-list-item ant-upload-list-item-done"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {isPdfFileUrl(file.name) && (
                        <FilePdfOutlined
                          style={{ fontSize: 32, color: '#e74c3c' }}
                        />
                      )}

                      {isImageFileUrl(file.name) && (
                        <Image
                          src={file.url}
                          width={50}
                          height={50}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                          placeholder={false}
                          preview={{
                            mask: (
                              <EyeOutlined
                                style={{ fontSize: 16, color: 'white' }}
                              />
                            ),
                          }}
                        />
                      )}

                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={file.name}
                        style={{
                          marginLeft: 8,
                          flex: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {file.name}
                      </a>

                      <ActionIcon
                        onClick={handleRemoveImage}
                        disabled={props.isLoading}
                      >
                        <DeleteOutlined
                          style={{ marginLeft: 12, cursor: 'pointer' }}
                        />
                      </ActionIcon>
                    </div>
                  )}
                  beforeUpload={() => false}
                />
              </Col>
            )}
          </>
        )}

        <Col span={24}>
          <Form.Item
            noStyle
            name="visibility"
            label={intl.formatMessage({
              id: 'workOrders.domain.observations.visibility',
            })}
            rules={[defaultEmptyFieldError]}
            initialValue={WorkOrderObservationVisibility.All}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              size="large"
              disabled={props.isLoading}
            >
              {Object.values(WorkOrderObservationVisibility).map((item) => (
                <Radio value={item}>
                  {intl.formatMessage({ id: `workOrders.${item}` })}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

export default WorkOrderObservationForm;
